import React, { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import html2pdf from "html2pdf.js";
import { db } from "../../firebaseConfig"; // Ensure this points to your Firebase config
import { collection, getDocs } from "firebase/firestore";

/* ------------ Society constants ------------ */
const SOCIETY = {
  name: "Shine Agri Allied & Marketing Multistate Cooperative Society Ltd.",
  address:
    "48, 1st Floor, Amar Arcade, Nilpur, Taluka Dantiwada, Banaskantha, Gujarat - 385505",
};

const MEMBERS_COLLECTION = "members";

/* ------------ Small UI helpers ------------ */
function TextRow({ label, value }) {
  return (
    <div className="w-full">
      <div className="text-sm font-medium text-gray-800">{label}</div>
      <div className="border-b border-gray-400 min-h-[28px] text-sm pb-1 text-gray-900">
        {value || "\u00A0"}
      </div>
    </div>
  );
}

function Select({ label, options, value, onChange, placeholder }) {
  return (
    <div className="w-full">
      <div className="text-xs font-medium text-gray-800 mb-1">{label}</div>
      <select
        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
        value={value || ""}
        onChange={(e) => onChange(e.target.value || null)}
      >
        <option value="" className="text-gray-500">{placeholder || "-- Select --"}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value} className="text-gray-900">
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/* ---- Pretty Section wrapper + Divider ---- */
function Section({ title, children }) {
  return (
    <div className="border border-gray-300 rounded-lg p-6 mb-6 bg-white shadow-sm">
      {title && (
        <div className="font-semibold text-base text-gray-900 mb-4 border-b border-gray-200 pb-2">
          {title}
        </div>
      )}
      {children}
    </div>
  );
}

function Divider() {
  return <div className="my-4 border-t border-gray-200" />;
}

/* ------------ Normalize any member schema ------------ */
function normalizeMember(raw, id) {
  const fullName =
    raw.fullName ||
    raw.name ||
    raw.memberName ||
    raw.candidateName ||
    raw.displayName ||
    "";

  const membershipId =
    raw.membershipId ||
    raw.member_id ||
    raw.membership_no ||
    raw.membershipNo ||
    raw.memberId ||
    "";

  const serialNo =
    raw.serialNo || raw.serial || raw.register_serial || raw.regSerial || "";

  const fatherOrSpouse =
    raw.fatherOrSpouse ||
    raw.father_name ||
    raw.fatherName ||
    raw.spouse ||
    raw.spouseName ||
    "";

  const address =
    raw.address ||
    raw.addr ||
    [raw.addressLine1, raw.addressLine2, raw.city, raw.state, raw.pincode]
      .filter(Boolean)
      .join(", ");

  let category = "";
  if (raw.category) category = raw.category;
  else if (raw.memberCategory) category = raw.memberCategory;
  else if (raw.caste) category = raw.caste;
  else if (raw.gender === "Female") category = "Women";
  else if (raw.gender) category = raw.gender;

  return {
    id,
    fullName,
    membershipId,
    serialNo,
    fatherOrSpouse,
    address,
    category,
    _raw: raw,
  };
}

/* ---------- Sorting helpers (series order) ---------- */
function extractTrailingNumberFromMembership(mid) {
  if (!mid) return null;
  const m = String(mid).match(/(\d+)\s*$/);
  return m ? Number(m[1]) : null;
}

function toNumberOrNull(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function seriesCompare(a, b) {
  const aMid = extractTrailingNumberFromMembership(a.membershipId);
  const bMid = extractTrailingNumberFromMembership(b.membershipId);
  if (aMid !== null || bMid !== null) {
    if (aMid === null) return 1;
    if (bMid === null) return -1;
    if (aMid !== bMid) return aMid - bMid;
  }
  const aSer = toNumberOrNull(a.serialNo);
  const bSer = toNumberOrNull(b.serialNo);
  if (aSer !== null || bSer !== null) {
    if (aSer === null) return 1;
    if (bSer === null) return -1;
    if (aSer !== bSer) return aSer - bSer;
  }
  return (a.fullName || "").localeCompare(b.fullName || "");
}

/* ------------ Main Component ------------ */
export default function NominationFormIII() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [candidateId, setCandidateId] = useState("");
  const [prop1Id, setProp1Id] = useState("");
  const [prop2Id, setProp2Id] = useState("");
  const [remarks, setRemarks] = useState("");

  const printRef = useRef(null);
  const today = useMemo(() => dayjs().format("DD/MM/YYYY"), []);

  useEffect(() => {
    async function loadMembers() {
      setLoading(true);
      try {
        const snap = await getDocs(collection(db, MEMBERS_COLLECTION));
        const list = snap.docs.map((d) => normalizeMember(d.data(), d.id));
        list.sort(seriesCompare);
        console.log("Members fetched:", list.length, list.slice(0, 3));
        setMembers(list);
      } catch (err) {
        console.error("Firestore read failed:", err);
        window.alert(
          'Could not load members. Check collection name "members" and Firestore rules.'
        );
      } finally {
        setLoading(false);
      }
    }
    loadMembers();
  }, []);

  const memberOptions = useMemo(() => {
    return members.map((m) => {
      const idPart =
        m.membershipId || m.serialNo || (m.id ? m.id.slice(0, 8) : "No-ID");
      const namePart =
        m.fullName && m.fullName.trim().length > 0 ? m.fullName : "Unnamed";
      return { value: m.id, label: `${namePart} — ${idPart}` };
    });
  }, [members]);

  const findById = (id) => members.find((m) => m.id === id) || null;
  const candidate = findById(candidateId);
  const prop1 = findById(prop1Id);
  const prop2 = findById(prop2Id);

  const errors = useMemo(() => {
    const e = [];
    if (!candidate) e.push("Please select a Candidate.");
    if (!prop1) e.push("Please select Proposer 1.");
    if (!prop2) e.push("Please select Proposer 2.");
    if (candidate && prop1 && candidate.id === prop1.id)
      e.push("Proposer 1 must be different from Candidate.");
    if (candidate && prop2 && candidate.id === prop2.id)
      e.push("Proposer 2 must be different from Candidate.");
    if (prop1 && prop2 && prop1.id === prop2.id)
      e.push("Proposer 1 and Proposer 2 must be different persons.");
    return e;
  }, [candidate, prop1, prop2]);

  const handleDownload = async () => {
    if (errors.length) {
      window.alert(
        "Please fix the following before download:\n\n" + errors.join("\n")
      );
      return;
    }
    const el = printRef.current;
    const fileName =
      "Form-III_Nomination_" +
      (candidate?.fullName || "Candidate") +
      "_" +
      dayjs().format("YYYYMMDD_HHmm") +
      ".pdf";

    const opt = {
      margin: [10, 10, 10, 10],
      filename: fileName,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      pagebreak: { mode: ["avoid-all", "css", "legacy"] },
    };

    await html2pdf().from(el).set(opt).save();
  };

  const Header = () => (
    <div className="text-center mb-8">
      <div className="text-xs font-semibold text-gray-600 tracking-wider">FORM III (See Rule 19)</div>
      <div className="font-bold text-xl text-gray-900 mt-2">NOMINATION FORM FOR ELECTIONS OF BOARD OF DIRECTORS</div>
      <div className="text-sm text-gray-700 mt-1">{SOCIETY.name}</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white p-6 font-sans">
      <style>
        {`
          @media print {
            body { margin: 0; padding: 0; }
            .no-print { display: none; }
            .print-page { 
              width: 100%;
              min-height: 297mm;
              margin: 0;
              padding: 10mm;
              box-sizing: border-box;
              font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif;
            }
            @page { 
              size: A4;
              margin: 10mm;
            }
          }
        `}
      </style>
      <div className="max-w-5xl mx-auto">
        <div className="bg-white text-gray-900 rounded-2xl shadow-2xl p-8 no-print">
          <div className="flex items-center justify-between gap-4">
            <h1 className="text-2xl font-bold text-gray-900">Form III - Nomination Auto-Fill</h1>
            <button
              onClick={handleDownload}
              className="px-6 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold disabled:opacity-50 transition-colors"
              disabled={loading}
            >
              Download PDF
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <Select
              label="Candidate (Member)"
              options={memberOptions}
              value={candidateId}
              onChange={setCandidateId}
              placeholder="Select candidate"
            />
            <Select
              label="Proposer 1 (Member)"
              options={memberOptions}
              value={prop1Id}
              onChange={setProp1Id}
              placeholder="Select proposer 1"
            />
            <Select
              label="Proposer 2 (Member)"
              options={memberOptions}
              value={prop2Id}
              onChange={setProp2Id}
              placeholder="Select proposer 2"
            />
          </div>

          {!loading && members.length === 0 && (
            <div className="mt-4 text-sm text-orange-700 bg-orange-50 border border-orange-200 rounded-lg p-3">
              No members found. Ensure collection "members" has at least: name/fullName and membershipId (or serialNo).
            </div>
          )}

          {loading && <div className="mt-4 text-sm text-gray-600">Loading members…</div>}

          {!!errors.length && (
            <div className="mt-4 bg-red-50 text-red-700 border border-red-200 rounded-lg p-3 text-sm">
              <ul className="list-disc pl-5">
                {errors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          )}

          <div ref={printRef} className="mt-8 bg-white text-black p-8 rounded-xl print-page">
            <Header />

            <Section title="Member & Society Details">
              <div className="space-y-4">
                <TextRow
                  label="1. Name of the Multi-State Co-operative Society:"
                  value={SOCIETY.name}
                />
                <TextRow label="2. Society and Address:" value={SOCIETY.address} />
                <Divider />
                <TextRow
                  label="3. Name of the candidate (Individual):"
                  value={candidate?.fullName || ""}
                />
                <TextRow
                  label="4. Membership No. / Serial No.:"
                  value={
                    candidate
                      ? candidate.membershipId || candidate.serialNo || ""
                      : ""
                  }
                />
                <TextRow
                  label="5. Father's/Mother's/Spouse's Name:"
                  value={candidate?.fatherOrSpouse || ""}
                />
                <TextRow
                  label="6. Address of the Candidate:"
                  value={candidate?.address || ""}
                />
              </div>
            </Section>

            <Section title="Candidate's Declaration">
              <div className="text-sm leading-relaxed text-gray-800">
                I, <span className="font-semibold">{candidate?.fullName || "________"}</span>,
                s/o <span className="font-semibold">{candidate?.fatherOrSpouse || "________"}</span>, Member No.{" "}
                <span className="font-semibold">
                  {candidate ? candidate.membershipId || candidate.serialNo || "________" : "________"}
                </span>{" "}
                of <span className="font-semibold">{SOCIETY.name}</span> hereby declare that I am willing to
                stand for election and that, to the best of my knowledge and belief, I am qualified and
                have not incurred any disqualification for membership of the Board of the said
                multi-state co-operative society under the Act, the Rules made thereunder and the
                Bye-laws of the society.
              </div>
              <div className="flex items-center gap-6 mt-6">
                <div className="text-sm text-gray-800">Date: {today}</div>
                <div className="flex-1" />
                <div className="text-sm text-right text-gray-800">
                  Signature of Candidate
                  <div className="h-10 border-b border-gray-400" />
                </div>
              </div>
            </Section>

            <div
              className="grid grid-cols-1 md:grid-cols-2 gap-10 no-break"
              style={{ pageBreakBefore: "always" }}
              data-html2pdf-pagebreak="before"
            >
              <Section title="7–9. Proposer Details">
                <TextRow label="Name:" value={prop1?.fullName || ""} />
                <TextRow
                  label="8. Serial no. of the proposer in the Register of Members:"
                  value={prop1 ? prop1.membershipId || prop1.serialNo || "" : ""}
                />
                <TextRow label="Address:" value={prop1?.address || ""} />
                <Divider />
                <div>
                  <div className="text-sm text-gray-800">9. Signature of the proposer</div>
                  <div className="h-12 border-b border-gray-400" />
                </div>
              </Section>

              <Section title="10–12. Seconder Details">
                <TextRow label="Name:" value={prop2?.fullName || ""} />
                <TextRow
                  label="11. Serial no. of the seconder in the Register of Members:"
                  value={prop2 ? prop2.membershipId || prop2.serialNo || "" : ""}
                />
                <TextRow label="Address:" value={prop2?.address || ""} />
                <Divider />
                <div>
                  <div className="text-sm text-gray-800">12. Signature of the seconder</div>
                  <div className="h-12 border-b border-gray-400" />
                </div>
              </Section>
            </div>

            <Section title="Endorsement by the Returning Officer">
              <div className="text-sm leading-relaxed text-gray-800">
                This nomination form was presented to me in person by ____________________ or
                received by registered post on __________________ at ______ hrs.
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                <TextRow label="Place:" value="" />
                <TextRow label="Date:" value="" />
              </div>
              <Divider />
              <div>
                <div className="text-sm text-gray-800">
                  Signature of Returning Officer or person authorized by him
                </div>
                <div className="h-12 border-b border-gray-400" />
              </div>
            </Section>

            <div className="text-xs text-gray-500 mt-6">
              * Auto-filled from member register. Please verify details before submission.
              Signatures must be done in ink on the printed form.
            </div>
          </div>

          <div className="mt-6 no-print">
            <label className="text-sm font-medium text-gray-800">
              Internal Remarks (won't appear on final form)
            </label>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 min-h-[100px] text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Any notes for RO office use..."
            />
          </div>
        </div>
      </div>
    </div>
  );
}